<template>
  <div class="list-item-file smd-panel m-b-20"
       @click="viewFile">
    <div class="file">
      <span class="file-icon ns-x-large-icon" :class="computedClassIcon"/>


      <div class="text" :class="{'m-l-10': !$isDesktop}">
        <h2 class="list-item-file__title" :class="!$isDesktop ? 'm-b-10 Title1' : 'm-b-20 Title2'">
          {{ computedTitle(fileData) }}
        </h2>
        <text-string :label-id="$isDesktop ? 'txt040__desktop' : 'txt040'"
                     :props="{strings: [fileData.patientname, '']}"
                     :class="{'m-b-5': $isDesktop}"/>
        <p class="gray-400" :class="$isDesktop ? 'Label1' : 'Body3'">{{ formattedDate }}</p>
      </div>
    </div>
    <div class="flex-row-center-xy-spaced m-t-15"
         v-if="!hideCoupon && !$isDesktop">
      <smd-coupon v-for="coupon in fileData.coupons"
                  small
                  :key="coupon.couponid"
                  :item="coupon"
                  :title="coupon.shortname"
                  :image="couponImage"/>
    </div>
    <div class="smd-panel__footer"
         :class="[{'list-item-file__right_btns': $isDesktop}, {'m-t-20': !$isDesktop}]"
         v-if="showButtons">
      <div class="flex-row-center-xy-spaced list-item-file__coupon"
           :class="$isDesktop ? 'm-b-20': 'm-b-5'"
           v-if="!hideCoupon && $isDesktop">
        <smd-coupon v-for="coupon in fileData.coupons"
                    small
                    :key="coupon.couponid"
                    :item="coupon"
                    :title="coupon.shortname"
                    :image="couponImage"/>
      </div>
      <smd-btn class="list-item-file__view-btn"
               section="5"
               variant="left-enabled"
               v-else>
        <text-string label-id="txt060__desktop"/>
      </smd-btn>
      <smd-btn class="list-item-file__press-to-order-btn"
               section="5"
               :variant="computedRightBtnTextAndStyles.variant"
               @click.stop.prevent="rightActionBtnFn">
        <text-string :label-id="computedRightBtnTextAndStyles.labelid"
                     :props="computedRightBtnTextAndStyles.props"/>
      </smd-btn>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapMutations} from "vuex";
  import {PageTypes} from "@/reference";

  import moment from 'moment';
  import PreviewFileType from '@/reference/preview-file-type';
  import {COUPON_STATUS} from '@/reference/medicine';
  import Routes from "@/router/routes";
  import TitleMixin from '@/mixins/title-mixin';
  import SanofiTryitLogo from "@/assets/images/img010.png";

  import {NUMBER_OF_DAYS_AFTER_EXPIRY} from "@/reference/app-constants";
  import {
    GETTER_DOCTOR_PATIENT_PARTNER,
    MUTATE_ADD_ATTACHED_PRESCRIPTION,
  } from "@/store/types";
  import {
    getPrescriptionCoupon,
    canAvailCoupon,
    isCouponProgram,
  } from "@/utils/medicine";
  import {toPhilMoment} from '@/utils/dates';
  import {getCouponLogo} from '@/reference/coupon';

  export default {
    name: "ListItemFile",
    mixins: [TitleMixin],
    props: {
      fileData: {type: Object, required: false},
    },
    computed: {
      ...mapGetters({
        doctorPatientPartner: GETTER_DOCTOR_PATIENT_PARTNER,
      }),
      coupon() {
        const prescriptionCoupon = getPrescriptionCoupon(this.fileData);

        if (prescriptionCoupon && isCouponProgram(prescriptionCoupon)) return null;

        return prescriptionCoupon;
      },
      couponStartTime() {
        return this.coupon ? moment(this.coupon.issuedate) : null;
      },
      couponExpirationDate() {
        if (!this.couponStartTime) return '';

        const start = new Date(this.couponStartTime);
        const end = start.setDate(start.getDate() + NUMBER_OF_DAYS_AFTER_EXPIRY);
        return moment(end).format('MMMM D, YYYY');
      },
      couponIsExpired() {
        if (this.couponStartTime) {
          const start = new Date(this.couponStartTime);
          const now = new Date().getTime();

          const end = start.setDate(start.getDate() + NUMBER_OF_DAYS_AFTER_EXPIRY);
          const endDate = moment(end).format('MMMM D, YYYY HH:mm:ss');

          const passTime = new Date(endDate).getTime() - now;
          const daysLeft = Math.floor(passTime / (1000 * 60 * 60 * 24));
          return daysLeft < 0;
        }
        return true;
      },
      hideCoupon() {
        if (!this.coupon) return true;
        return this.couponIsExpired || this.coupon.status === COUPON_STATUS.ARCHIVED;
      },
      computedRightBtnTextAndStyles() {
        let result = '';
        if (this.pageType === PageTypes.prescription) {
          if (!canAvailCoupon(this.coupon) || this.couponIsExpired) {
            result = {
              labelid: 'txt265',
              variant: 'right-blue-enabled'
            }
          } else {
            result = {
              labelid: 'txt339',
              props: {strings: [this.couponExpirationDate]},
              variant: 'right-red-enabled'
            }
          }
        } else if (this.pageType === PageTypes.image) {
          result = {
            labelid: 'txt265',
            variant: 'right-blue-enabled'
          }
        } else if (this.pageType === PageTypes.labtest) {
          if (this.fileData.orderid) {
            result = {
              labelid: 'txt253',
              variant: 'right-green-enabled'
            }
          } else {
            result = {
              labelid: 'txt222',
              variant: 'right-red-enabled'
            }
          }
        }

        return result;
      },
      computedClassIcon() {
        switch (this.fileData.pagetype) {
          case 'prescription':
            return 'ns-icon-ico012'
          case 'labtest':
            return 'ns-icon-ico013'
          default:
            return 'ns-icon-ico014'
        }
      },
      formattedDate() {
        const dateReceived = toPhilMoment(this.fileData.received);
        const dateFormat = dateReceived.format('MMMM DD, YYYY');
        const timeFormat = dateReceived.format('h:mm a');
        return `Sent on ${dateFormat} at ${timeFormat}`;
      },
      showButtons() {
        // return this.pageType !== undefined &&
        // this.pageType !== PageTypes.customform ?
        //   // this.pageType === PageTypes.labtest ||
        //   this.pageType === PageTypes.image ||
        //   this.pageType === PageTypes.prescription
        //   : false
        return false;
      },
      pageType() {
        return this.fileData.pagetype;
      },
      prescriptionPageType() {
        let type = ''
        switch (this.pageType) {
          case PageTypes.prescription:
            type = PreviewFileType.PREVIEW_TYPE_PRESCRIPTION
            break;
          case PageTypes.image:
            type = PreviewFileType.PREVIEW_TYPE_PRESCRIPTION
            break;
        }

        return type
      },
      couponImage() {
        return getCouponLogo(this.coupon);
      },
    },
    methods: {
      ...mapMutations({
        addPrescription: MUTATE_ADD_ATTACHED_PRESCRIPTION
      }),
      rightActionBtnFn() {
        if (this.couponIsExpired) return;

        const {doctor, patient} = this.doctorPatientPartner[this.fileData.token];
        const prescription = {
          ...this.fileData,
          partial: false,
          includeOtherItems: true,
          remarks: '',
          doctorname: doctor._fullName,
          patientname: patient.name,
        };

        this.addPrescription(prescription);
        this.$router.push({
          name: Routes.PHARMACY_ORDERS,
        });
      },
      viewFile() {
        const prescriptionFile = this.coupon ? this.fileData : {...this.fileData, coupons: []};

        this.$router.push(
          {
            name: Routes.PREVIEW_FILE,
            params: {
              file: [prescriptionFile],
              title: this.computedTitle(this.fileData),
              filesView: true,
              type: this.prescriptionPageType
            },
          }
        )
      },
    },
    SanofiTryitLogo
  }
</script>

<style lang="scss">
  .list-item-file {
    @extend %layout--flex-col;
    @include responsive(desktop) {
      display: grid;
      grid-template-columns: 1fr 235px;
      align-items: center;
      padding: space(m-base);
      min-height: 160px;
      overflow: hidden;
    }

    cursor: pointer;

    .file {
      @extend %layout--flex-row;
      flex-direction: row;
      align-items: normal;

      @include responsive(desktop) {
        align-items: center;
      }
    }

    .file-icon {
      @include responsive(desktop) {
        font-size: 120px !important;
        margin-right: space(m-base);
      }
    }

    .download-icon {
      align-self: center;
      color: color(gray, text);

      p {
        font-size: 24px;
      }
    }
  }

  .list-item-file__title {
    line-height: 1;
  }

  .list-item-file__right_btns {
    flex-direction: column;
    margin: unset;

    > button {
      width: 100%;
      max-width: 235px;
      min-width: 235px;
    }
  }

  .list-item-file__view-btn, .list-item-file__coupon {
    @extend %layout--flex-center-xy;
    @include responsive(desktop) {
      position: absolute;
      top: 0;
      right: 0;
      height: 90px !important;
      width: 235px !important;
    }
  }

  .list-item-file__press-to-order-btn {
    @include responsive(desktop) {
      position: absolute;
      bottom: 0 !important;
      right: 0 !important;
    }
  }
</style>